import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ChevronRight, ChevronLeft, Save, Plus, Trash2,
  Heart, Shield, Users, Star, Sparkles,
} from "lucide-react";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const NEED_ICONS = {
  selfActualization: Sparkles,
  esteem: Star,
  belonging: Users,
  safety: Shield,
  physiological: Heart,
};

// Pyramid is displayed top-to-bottom in this order (top = self-actualization)
const PYRAMID_ORDER = ["selfActualization", "esteem", "belonging", "safety", "physiological"];

// Color tokens for each level (match user's reference image)
const NEED_STYLE = {
  selfActualization: {
    bar: "bg-amber-300 hover:bg-amber-400",
    barActive: "bg-amber-400 ring-2 ring-amber-500 ring-offset-2",
    chip: "bg-amber-50 text-amber-700 border-amber-200",
    icon: "bg-amber-100 text-amber-700",
  },
  esteem: {
    bar: "bg-orange-400 hover:bg-orange-500",
    barActive: "bg-orange-500 ring-2 ring-orange-600 ring-offset-2",
    chip: "bg-orange-50 text-orange-700 border-orange-200",
    icon: "bg-orange-100 text-orange-700",
  },
  belonging: {
    bar: "bg-cyan-400 hover:bg-cyan-500",
    barActive: "bg-cyan-500 ring-2 ring-cyan-600 ring-offset-2",
    chip: "bg-cyan-50 text-cyan-700 border-cyan-200",
    icon: "bg-cyan-100 text-cyan-700",
  },
  safety: {
    bar: "bg-blue-500 hover:bg-blue-600",
    barActive: "bg-blue-600 ring-2 ring-blue-700 ring-offset-2",
    chip: "bg-blue-50 text-blue-700 border-blue-200",
    icon: "bg-blue-100 text-blue-700",
  },
  physiological: {
    bar: "bg-rose-500 hover:bg-rose-600",
    barActive: "bg-rose-600 ring-2 ring-rose-700 ring-offset-2",
    chip: "bg-rose-50 text-rose-700 border-rose-200",
    icon: "bg-rose-100 text-rose-700",
  },
};

// Pyramid layout: bars are wider on desktop. On mobile, all bars use full width
// (compact "pills" with name only) to keep the visualization legible.
const PYRAMID_WIDTHS = {
  selfActualization: "w-full sm:w-1/3",
  esteem: "w-full sm:w-1/2",
  belonging: "w-full sm:w-2/3",
  safety: "w-full sm:w-5/6",
  physiological: "w-full",
};

const emptyRow = () => ({
  id: `r_${Math.random().toString(36).slice(2, 10)}`,
  need: "",
  nourished: "",      // 'yes' | 'no' | 'partial'
  response: "",       // how to respond
  priority: "",       // 'high' | 'medium' | 'low'
  deadline: "",       // free text or date
});

const NeedsExplorationPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);

  // categories ordered bottom-to-top (matching the conceptual hierarchy used in code)
  const MASLOW_NEEDS = [
    {
      id: "physiological",
      name: t("needs.physiological"),
      description: t("needs.physiological.description"),
      examples: t("needs.physiological.examples"),
      questions: [t("needs.physiological.q1"), t("needs.physiological.q2"), t("needs.physiological.q3"), t("needs.physiological.q4")],
    },
    {
      id: "safety",
      name: t("needs.safety"),
      description: t("needs.safety.description"),
      examples: t("needs.safety.examples"),
      questions: [t("needs.safety.q1"), t("needs.safety.q2"), t("needs.safety.q3"), t("needs.safety.q4")],
    },
    {
      id: "belonging",
      name: t("needs.belonging"),
      description: t("needs.belonging.description"),
      examples: t("needs.belonging.examples"),
      questions: [t("needs.belonging.q1"), t("needs.belonging.q2"), t("needs.belonging.q3"), t("needs.belonging.q4")],
    },
    {
      id: "esteem",
      name: t("needs.esteem"),
      description: t("needs.esteem.description"),
      examples: t("needs.esteem.examples"),
      questions: [t("needs.esteem.q1"), t("needs.esteem.q2"), t("needs.esteem.q3"), t("needs.esteem.q4")],
    },
    {
      id: "selfActualization",
      name: t("needs.selfActualization"),
      description: t("needs.selfActualization.description"),
      examples: t("needs.selfActualization.examples"),
      questions: [t("needs.selfActualization.q1"), t("needs.selfActualization.q2"), t("needs.selfActualization.q3"), t("needs.selfActualization.q4")],
    },
  ];

  const [currentLevel, setCurrentLevel] = useState(0);
  const [ratings, setRatings] = useState({});
  const [reflections, setReflections] = useState({});
  // needs_table: { [categoryId]: [emptyRow(), ...] }
  const [needsTable, setNeedsTable] = useState(() => {
    const init = {};
    MASLOW_NEEDS.forEach((n) => { init[n.id] = [emptyRow()]; });
    return init;
  });
  const [submitting, setSubmitting] = useState(false);

  const currentNeed = MASLOW_NEEDS[currentLevel];
  const Icon = NEED_ICONS[currentNeed.id];
  const style = NEED_STYLE[currentNeed.id];

  const handleRating = (questionIdx, value) => {
    setRatings((prev) => ({ ...prev, [`${currentNeed.id}_${questionIdx}`]: value[0] }));
  };

  const setRow = (cat, rowId, field, value) => {
    setNeedsTable((prev) => ({
      ...prev,
      [cat]: prev[cat].map((r) => (r.id === rowId ? { ...r, [field]: value } : r)),
    }));
  };

  const addRow = (cat) => {
    setNeedsTable((prev) => ({ ...prev, [cat]: [...prev[cat], emptyRow()] }));
  };

  const removeRow = (cat, rowId) => {
    setNeedsTable((prev) => ({
      ...prev,
      [cat]: prev[cat].length > 1 ? prev[cat].filter((r) => r.id !== rowId) : prev[cat],
    }));
  };

  // Levels list for pyramid display (top -> bottom)
  const levelsForPyramid = PYRAMID_ORDER.map((id) =>
    MASLOW_NEEDS.find((m) => m.id === id),
  );

  const goToLevelId = (id) => {
    const idx = MASLOW_NEEDS.findIndex((m) => m.id === id);
    if (idx >= 0) setCurrentLevel(idx);
  };

  // Clean rows for submission (remove empty rows)
  const cleanNeedsTable = () => {
    const out = {};
    Object.entries(needsTable).forEach(([cat, rows]) => {
      const cleaned = rows
        .filter((r) => (r.need && r.need.trim()) || (r.response && r.response.trim()))
        .map(({ id, ...rest }) => rest); // eslint-disable-line no-unused-vars
      if (cleaned.length) out[cat] = cleaned;
    });
    return out;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const cleaned = cleanNeedsTable();
      const res = await axios.post(
        `${API}/needs-exploration`,
        { ratings, reflections, needs_table: cleaned },
        { withCredentials: true },
      );
      toast.success(t("needs.saved"));
      if (user?.role === "patient") promptShare(res.data, "needs_exploration");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="needs-exploration-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("needs.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("needs.pageSubtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.needs_exploration} />

        {/* Interactive Maslow Pyramid */}
        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-center">
              {t("needs.pyramidTitle")}
            </CardTitle>
            <p className="text-xs text-muted-foreground text-center">
              {t("needs.pyramidHint")}
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-1.5" data-testid="maslow-pyramid">
              {levelsForPyramid.map((lvl) => {
                const isActive = currentNeed.id === lvl.id;
                const s = NEED_STYLE[lvl.id];
                const LvlIcon = NEED_ICONS[lvl.id];
                return (
                  <button
                    key={lvl.id}
                    onClick={() => goToLevelId(lvl.id)}
                    data-testid={`pyramid-level-${lvl.id}`}
                    className={`${PYRAMID_WIDTHS[lvl.id]} ${isActive ? s.barActive : s.bar} text-white rounded transition-all px-3 py-3 sm:py-4 flex items-center justify-center gap-2 text-center`}
                  >
                    <LvlIcon className="w-4 h-4 flex-shrink-0" />
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-xs sm:text-sm leading-tight">{lvl.name}</span>
                      <span className="text-[10px] sm:text-xs opacity-90 leading-tight hidden sm:block">
                        {lvl.examples}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Current level details */}
        <Card className="max-w-4xl mx-auto" data-testid="current-level-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <div className={`w-10 h-10 rounded-lg ${style.icon} flex items-center justify-center`}>
                <Icon className="w-5 h-5" />
              </div>
              {currentNeed.name}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{currentNeed.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {currentNeed.examples.split("/").map((ex, i) => (
                <span
                  key={i}
                  className={`text-xs px-2 py-0.5 rounded-full border ${style.chip}`}
                >
                  {ex.trim()}
                </span>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Sliders 1-10 */}
            <div className="space-y-6">
              <h3 className="font-medium text-sm">{t("needs.evaluateSection")}</h3>
              {currentNeed.questions.map((question, qIdx) => (
                <div key={qIdx} className="space-y-3">
                  <p className="text-sm">{question}</p>
                  <div className="px-2">
                    <Slider
                      value={[ratings[`${currentNeed.id}_${qIdx}`] ?? 5]}
                      onValueChange={(v) => handleRating(qIdx, v)}
                      min={1}
                      max={10}
                      step={1}
                      data-testid={`rating-${currentNeed.id}-${qIdx}`}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>{t("needs.notSatisfied")}</span>
                      <span>{ratings[`${currentNeed.id}_${qIdx}`] ?? 5}/10</span>
                      <span>{t("needs.fullySatisfied")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Reflection textarea */}
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("needs.yourReflections")}</label>
              <Textarea
                placeholder={t("needs.reflectionsPlaceholder")}
                value={reflections[currentNeed.id] || ""}
                onChange={(e) => setReflections((p) => ({ ...p, [currentNeed.id]: e.target.value }))}
                rows={3}
                data-testid={`reflection-${currentNeed.id}`}
              />
            </div>

            {/* Specific needs - responsive: cards on mobile, table on desktop */}
            <div className="space-y-3">
              <div>
                <h3 className="font-medium text-sm">{t("needs.tableTitle")}</h3>
                <p className="text-xs text-muted-foreground">{t("needs.tableHint")}</p>
              </div>

              {/* Mobile: stacked cards (one card per need) */}
              <div className="space-y-3 sm:hidden" data-testid={`needs-cards-${currentNeed.id}`}>
                {needsTable[currentNeed.id].map((row, rIdx) => (
                  <div
                    key={row.id}
                    className="rounded-lg border bg-muted/30 p-3 space-y-2.5"
                    data-testid={`needs-row-${currentNeed.id}-${rIdx}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                        {t("needs.addRow")} {rIdx + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={needsTable[currentNeed.id].length === 1}
                        onClick={() => removeRow(currentNeed.id, row.id)}
                        data-testid={`need-remove-${currentNeed.id}-${rIdx}`}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </Button>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium">{t("needs.col.specific")}</label>
                      <Input
                        value={row.need}
                        onChange={(e) => setRow(currentNeed.id, row.id, "need", e.target.value)}
                        placeholder={t("needs.col.specific.placeholder")}
                        data-testid={`need-input-${currentNeed.id}-${rIdx}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t("needs.col.nourished")}</label>
                        <Select
                          value={row.nourished}
                          onValueChange={(v) => setRow(currentNeed.id, row.id, "nourished", v)}
                        >
                          <SelectTrigger data-testid={`need-nourished-${currentNeed.id}-${rIdx}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">{t("needs.nourished.yes")}</SelectItem>
                            <SelectItem value="partial">{t("needs.nourished.partial")}</SelectItem>
                            <SelectItem value="no">{t("needs.nourished.no")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium">{t("needs.col.priority")}</label>
                        <Select
                          value={row.priority}
                          onValueChange={(v) => setRow(currentNeed.id, row.id, "priority", v)}
                        >
                          <SelectTrigger data-testid={`need-priority-${currentNeed.id}-${rIdx}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">{t("needs.priority.high")}</SelectItem>
                            <SelectItem value="medium">{t("needs.priority.medium")}</SelectItem>
                            <SelectItem value="low">{t("needs.priority.low")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium">{t("needs.col.deadline")}</label>
                      <Input
                        type="date"
                        value={row.deadline}
                        onChange={(e) => setRow(currentNeed.id, row.id, "deadline", e.target.value)}
                        data-testid={`need-deadline-${currentNeed.id}-${rIdx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium">{t("needs.col.response")}</label>
                      <Textarea
                        value={row.response}
                        onChange={(e) => setRow(currentNeed.id, row.id, "response", e.target.value)}
                        placeholder={t("needs.col.response.placeholder")}
                        rows={2}
                        data-testid={`need-response-${currentNeed.id}-${rIdx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: full table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full border-collapse text-sm" data-testid={`needs-table-${currentNeed.id}`}>
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left p-2 font-medium w-1/4">{t("needs.col.specific")}</th>
                      <th className="text-left p-2 font-medium w-32">{t("needs.col.nourished")}</th>
                      <th className="text-left p-2 font-medium">{t("needs.col.response")}</th>
                      <th className="text-left p-2 font-medium w-28">{t("needs.col.priority")}</th>
                      <th className="text-left p-2 font-medium w-28">{t("needs.col.deadline")}</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {needsTable[currentNeed.id].map((row, rIdx) => (
                      <tr key={row.id} className="border-b align-top">
                        <td className="p-2">
                          <Input
                            value={row.need}
                            onChange={(e) => setRow(currentNeed.id, row.id, "need", e.target.value)}
                            placeholder={t("needs.col.specific.placeholder")}
                          />
                        </td>
                        <td className="p-2">
                          <Select
                            value={row.nourished}
                            onValueChange={(v) => setRow(currentNeed.id, row.id, "nourished", v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">{t("needs.nourished.yes")}</SelectItem>
                              <SelectItem value="partial">{t("needs.nourished.partial")}</SelectItem>
                              <SelectItem value="no">{t("needs.nourished.no")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Textarea
                            value={row.response}
                            onChange={(e) => setRow(currentNeed.id, row.id, "response", e.target.value)}
                            placeholder={t("needs.col.response.placeholder")}
                            rows={2}
                          />
                        </td>
                        <td className="p-2">
                          <Select
                            value={row.priority}
                            onValueChange={(v) => setRow(currentNeed.id, row.id, "priority", v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">{t("needs.priority.high")}</SelectItem>
                              <SelectItem value="medium">{t("needs.priority.medium")}</SelectItem>
                              <SelectItem value="low">{t("needs.priority.low")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Input
                            type="date"
                            value={row.deadline}
                            onChange={(e) => setRow(currentNeed.id, row.id, "deadline", e.target.value)}
                          />
                        </td>
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={needsTable[currentNeed.id].length === 1}
                            onClick={() => removeRow(currentNeed.id, row.id)}
                          >
                            <Trash2 className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => addRow(currentNeed.id)}
                data-testid={`add-need-${currentNeed.id}`}
              >
                <Plus className="w-4 h-4 mr-1" /> {t("needs.addRow")}
              </Button>
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="outline"
                disabled={currentLevel === 0}
                onClick={() => setCurrentLevel(currentLevel - 1)}
                data-testid="needs-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> {t("common.previous")}
              </Button>
              {currentLevel < MASLOW_NEEDS.length - 1 ? (
                <Button onClick={() => setCurrentLevel(currentLevel + 1)} data-testid="needs-next">
                  {t("common.next")} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-needs"
                >
                  <Save className="w-4 h-4 mr-1" /> {submitting ? t("exercise.saving") : t("needs.saveExploration")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <ShareExerciseDialog
        open={!!shareData}
        entryId={shareData?.entryId}
        exerciseType={shareData?.exerciseType}
        onClose={closeSharePrompt}
      />
    </DashboardLayout>
  );
};

export default NeedsExplorationPage;
